import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, Clock, CreditCard, Video, MessageCircle, Send, ArrowLeft, RefreshCw } from 'lucide-react';
import MeetRoom from '../pages/MeetPage/MeetRoom';
import '../pages/MeetPage/MeetPage.css';
import './ConsultationDetail.css';
export const consultationStatuses = {
  pending: 'Ожидает подтверждения',
  confirmed: 'Подтверждена · ожидает оплаты',
  paid: 'Оплачена',
  completed: 'Проведена',
  cancelled: 'Отменена',
  rejected: 'Не подтверждена'
};
export const paymentStatuses = {
  unpaid: 'Не оплачена',
  pending: 'Ожидает оплаты',
  paid: 'Оплачена',
  partial: 'Оплачена частично',
  test_paid: 'Оплачена · тестовая отметка',
  failed: 'Платёж не завершён',
  refunded: 'Возвращена',
  partially_refunded: 'Частичный возврат',
  refund_required: 'Требуется согласовать возврат'
};
const chronologicalMessages = (a,b) => Date.parse(a.created_at)-Date.parse(b.created_at) || Number(a.id)-Number(b.id);
const errorMessage = e => e.response?.data?.message || (['ECONNABORTED', 'ETIMEDOUT'].includes(e.code) ? 'Сервер не ответил вовремя. Обновите страницу, чтобы проверить, сохранились ли изменения.' : 'Не удалось выполнить запрос. Попробуйте ещё раз.');
function MessageBody({body}) {
  return <p>{String(body || '').split(/(https?:\/\/[^\s<>]+)/g).map((part,index)=>/^https?:\/\//.test(part) ? <a key={index} href={part} target="_blank" rel="noopener noreferrer">{part}</a> : part)}</p>;
}
export function ConsultationCard({ booking: b, to, specialist = false }) {
  return <Link to={to} className="consult-list-card">
    <div className="consult-list-date"><strong>{b.booking_date.slice(8)}</strong><span>{new Date(`${b.booking_date}T12:00:00`).toLocaleDateString('ru-RU', { month: 'short' })}</span></div>
    <div className="consult-list-info"><small>Консультация №{b.consultation_number}</small><h3>{specialist ? b.client_name : b.therapist_name}</h3><p><Clock size={15} /> {b.start_time.slice(0, 5)} МСК · {b.format === 'online' ? 'Онлайн' : 'Очно'}</p><span className={`consult-badge ${b.status}`}>{consultationStatuses[b.status]}</span></div>
    <div className="consult-list-price"><strong>{b.amount ? `${Number(b.amount).toLocaleString('ru-RU')} ₽` : 'Уточняется'}</strong><small>{paymentStatuses[b.payment_status]}</small><span aria-hidden="true">→</span></div>
  </Link>;
}
export function Conversation({
  endpoint,
  role,
  readOnly = false,
  onFirstMessage
}) {
  const [ready,setReady]=useState(false);
  const [messages, setMessages] = useState([]),
    [body, setBody] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const cursor = useRef('0'),
    end = useRef(null);
  const followMessages = useRef(true);
  useEffect(() => {
    let active = true;
    cursor.current = '0';
    followMessages.current = true;
    setReady(false);
    setMessages([]);
    setError('');
    let loading = false;
    const poll = async () => {
      if (loading) return;
      loading = true;
      try {
        let page;
        do {
          const {data} = await axios.get(endpoint, {params:{after:cursor.current},withCredentials:true,timeout:30000});
          page = data;
          if (!active) return;
          if (data.length) {
            cursor.current = data[data.length - 1].id;
            setMessages(prev => [...prev, ...data.filter(m => !prev.some(p => p.id === m.id))].sort(chronologicalMessages));
          }
        } while (page.length === 200 && active);
        if (active) {setError('');setReady(true);}
      } catch (e) {
        if (active) setError(errorMessage(e));
      } finally {
        loading = false;
      }
    };
    poll();
    const timer = setInterval(poll, 4000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [endpoint]);
  useEffect(() => {
    const list = end.current?.parentElement;
    if (list && followMessages.current) list.scrollTop = list.scrollHeight;
  }, [messages]);
  return <section className="consult-chat"><h2><MessageCircle size={20} /> Переписка</h2><p className="consult-muted">Переписка доступна участникам и администратору сервиса.</p><div className="consult-messages" onScroll={e=>{const list=e.currentTarget;followMessages.current=list.scrollHeight-list.scrollTop-list.clientHeight<80;}}>{!messages.length && <p className="consult-muted">{!ready?'Загрузка переписки…':readOnly?'Сообщений пока нет.':'Здесь можно обсудить время встречи и задать вопросы.'}</p>}{messages.map(m => <article className={`consult-message ${m.sender === role ? 'mine' : ''}`} key={m.id}><small>{m.sender === role ? 'Вы' : m.sender === 'specialist' ? 'Специалист' : m.sender === 'curator' ? 'Куратор' : 'Клиент'} · {new Date(m.created_at).toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}</small><MessageBody body={m.body}/></article>)}<div ref={end} /></div>{error && <p role="alert" className="consult-error">{error}</p>}{!readOnly && <form onSubmit={async e => {
      e.preventDefault();
      if (!body.trim() || busy || !ready) return;
      setBusy(true);
      try {
        const {
          data
        } = await axios.post(endpoint, {
          body
        }, {
          withCredentials: true,
          timeout: 30000
        });
        setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data].sort(chronologicalMessages));
        if(!messages.length) onFirstMessage?.();
        setBody('');
        setError('');
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    }}><textarea aria-label="Сообщение" value={body} onChange={e => setBody(e.target.value)} maxLength={4000} rows={2} placeholder="Написать сообщение…" required /><button disabled={busy || !ready || !body.trim()} aria-label="Отправить сообщение"><Send size={19} /></button></form>}</section>;
}
function PaymentForm({ endpoint, booking, offerUrl, onClose, onProfileUpdated }) {
  const dialog = useRef(null);
  const [phone, setPhone] = useState(''), [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false), [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  useEffect(() => {
    const node = dialog.current;
    node.showModal();
    let active = true;
    axios.get(`${endpoint}/payment-details`, { withCredentials: true }).then(({data}) => {
      if (active) { setPhone(data.phone); setEmail(data.email); }
    }).catch(e => { if (active) setError(errorMessage(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; node.close(); };
  }, [endpoint]);
  return createPortal(<dialog ref={dialog} className="consult-payment-dialog consultation-detail" aria-labelledby="payment-title" onCancel={e => { e.preventDefault(); if (!busy) onClose(); }}>
    <div className="consult-payment-header"><div><small>КОНСУЛЬТАЦИЯ №{booking.consultation_number}</small><h2 id="payment-title">Оплата консультации</h2></div><button type="button" className="secondary" disabled={busy} onClick={onClose} aria-label="Закрыть оплату">×</button></div>
    <div className="consult-payment-total"><span>{booking.therapist_name}</span><strong>{Number(booking.amount).toLocaleString('ru-RU')} ₽</strong></div>
    <form onSubmit={async e => {
      e.preventDefault(); if (busy || loading) return;
      setBusy(true); setError('');
      try {
        const {data} = await axios.post(`${endpoint}/pay`, {phone, consent}, {withCredentials: true, timeout: 120000});
        window.location.assign(data.paymentUrl);
      } catch (err) { setError(errorMessage(err)); }
      finally { setBusy(false); if (onProfileUpdated) Promise.resolve(onProfileUpdated()).catch(() => {}); }
    }}>
      {loading ? <p role="status">Загрузка данных для чека…</p> : <>
        <label>Телефон для чека<input type="tel" autoComplete="tel" required value={phone} maxLength={40} placeholder="+7 999 123-45-67" onChange={e => setPhone(e.target.value)} /></label>
        <p className="consult-muted">Телефон сохранится в вашем профиле. Чек отправим на {email || 'почту аккаунта'}.</p>
        <label className="consult-consent"><input type="checkbox" required checked={consent} onChange={e => setConsent(e.target.checked)} /> Принимаю <a href={offerUrl} target="_blank" rel="noreferrer">условия оплаты</a></label>
      </>}
      {error && <p role="alert" className="consult-error">{error}</p>}
      <button type="submit" className="consult-pay-submit" disabled={busy || loading || !consent || !phone.trim()}><CreditCard size={18}/>{busy ? 'Создаём счёт…' : 'Перейти к оплате'}</button>
    </form>
  </dialog>, document.body);
}
export default function ConsultationDetail({
  id,
  role = 'client',
  back = '/account',
  offerUrl = 'https://mag-gestalt.ru/mag/oferta/',
  onProfileUpdated,
  onRepeat,
  showConversation = true,
  backForBooking,
  backLabel = 'Мои консультации',
  apiBase
}) {
  const base = apiBase || `/api/agregator/${role}`,
    endpoint = `${base}/consultations/${id}`;
  const [booking, setBooking] = useState(null),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [session, setSession] = useState(null),
    [paymentOpen, setPaymentOpen] = useState(false),
    [edit, setEdit] = useState(null),
    [notes, setNotes] = useState('');
  const load = useCallback(async () => {
    const {
      data
    } = await axios.get(endpoint, {
      withCredentials: true,
      timeout: 30000
    });
    setBooking(data);
    return data;
  }, [endpoint]);
  useEffect(() => {
    let active = true;
    setBooking(null);
    setError('');
    axios.get(endpoint, {
      withCredentials: true,
      timeout: 30000
    }).then(({
      data
    }) => {
      if (active) {
        setBooking(data);
        setNotes(data.specialist_notes || '');
      }
    }).catch(e => {
      if (active) setError(errorMessage(e));
    });
    return () => {
      active = false;
    };
  }, [endpoint]);
  useEffect(() => {
    let active = true;
    if (role === 'specialist') axios.post(`${base}/payments/refresh`, {}, {
      withCredentials: true,
      timeout: 120000
    }).then(() => {
      if (active) return load();
    }).catch(() => {
      if (active) setNotice('Автоматическая проверка оплаты не удалась. Её можно повторить.');
    });
    const timer = setInterval(() => {
      if (active) load().catch(() => {});
    }, 10000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [base, role, load]);
  async function action(fn) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const post = (path, body = {}) => axios.post(`${endpoint}${path}`, body, {
    withCredentials: true,
    timeout: 120000
  });
  if (session) return createPortal(<div className="consultation-call"><MeetRoom session={session} onExit={() => setSession(null)} /></div>, document.body);
  if (!booking) return <div className="consultation-detail"><Link to={back}>← Назад</Link><p role={error ? 'alert' : 'status'}>{error || 'Загрузка консультации…'}</p></div>;
  const b = booking,
    active = ['pending', 'confirmed', 'paid'].includes(b.status);
  return <div className="consultation-detail"><Link className="consult-back" to={backForBooking?.(b) || back}><ArrowLeft size={17} /> {backLabel}</Link><header className="consult-title consult-hero"><div><p className="consult-muted">КОНСУЛЬТАЦИЯ №{b.consultation_number}</p><h1>{b.therapist_name}</h1></div><div className={`consult-status-banner ${b.status}`}><small>Статус консультации</small><strong>{consultationStatuses[b.status]}</strong></div></header>{error && <p className="consult-error" role="alert">{error}</p>}{notice && <p className="consult-notice" role="status">{notice}</p>}{paymentOpen && <PaymentForm endpoint={endpoint} booking={b} offerUrl={offerUrl} onClose={() => setPaymentOpen(false)} onProfileUpdated={onProfileUpdated} />}<div className="consult-layout"><div><section className="consult-panel"><h2>О встрече</h2><div className="consult-people"><div><span className="consult-person-avatar">{b.client_name?.slice(0, 1)}</span><div><small>Участник</small><strong>{b.client_name}</strong></div></div><div><span className="consult-person-avatar specialist">{b.therapist_name?.slice(0, 1)}</span><div><small>Специалист</small><strong>{b.therapist_name}</strong></div></div></div><div className="consult-facts"><div><Calendar size={21}/><small>Дата</small><strong>{new Date(b.booking_date + 'T12:00:00').toLocaleDateString('ru-RU', {day:'numeric',month:'long',year:'numeric'})}</strong></div><div><Clock size={21}/><small>Время · Москва</small><strong>{b.start_time.slice(0,5)} — {b.end_time.slice(0,5)}</strong></div><div><Video size={21}/><small>Формат встречи</small><strong>{b.format === 'online' ? 'Онлайн' : 'Очно'}</strong></div><div><CreditCard size={21}/><small>Стоимость</small><strong>{b.amount ? Number(b.amount).toLocaleString('ru-RU') + ' ₽' : 'Уточняется'}</strong><span>{paymentStatuses[b.payment_status]}</span></div></div>{b.topic && <p className="consult-topic">Тема: <strong>{b.topic}</strong></p>}{b.message && <div className="consult-request"><h3>Комментарий при записи</h3><p>{b.message}</p></div>}</section>{showConversation && (b.client_id ? <Conversation endpoint={`${endpoint}/messages`} role={role} /> : <p className="consult-muted">Клиент пока не подтвердил аккаунт Магрегатора. Для связи используйте указанные контакты.</p>)}</div><aside className="consult-side">{role === 'specialist' && <section className="consult-panel consult-contacts"><h2>Контакты клиента</h2><dl><div><dt>Телефон</dt><dd>{b.client_phone ? <a href={`tel:${b.client_phone.replace(/[^+0-9]/g, '')}`}>{b.client_phone}</a> : 'Не указан'}</dd></div><div><dt>Почта</dt><dd>{b.client_email ? <a href={`mailto:${b.client_email}`}>{b.client_email}</a> : 'Не указана'}</dd></div></dl></section>}<section className="consult-panel"><h2>Действия</h2>{role === 'client' && b.status === 'completed' && onRepeat && <button disabled={busy} onClick={() => onRepeat(b)}><RefreshCw size={18}/>Повторить</button>}{role === 'client' && b.status === 'confirmed' && ['unpaid', 'pending', 'failed'].includes(b.payment_status) && <><button disabled={busy} onClick={() => setPaymentOpen(true)}><CreditCard size={18} /> Оплатить</button><p className="consult-pay-note">После оплаты в течение нескольких минут нажмите «Проверить оплаты». Не совершайте один и тот же платёж несколько раз.</p></>}<button className="secondary" disabled={busy} onClick={() => action(async () => {
            await axios.post(`${base}/payments/refresh`, {}, {
              withCredentials: true,
              timeout: 120000
            });
            setNotice('Платежи проверены.');
          })}><RefreshCw size={16} /> Проверить оплаты</button>{b.meeting_id && ['confirmed', 'paid'].includes(b.status) && <button disabled={busy} onClick={() => action(async () => {
            setSession((await post('/meeting/join')).data);
          })}><Video size={18} /> Войти во встречу</button>}{role === 'specialist' && !b.meeting_id && b.client_id && b.format === 'online' && ['confirmed', 'paid'].includes(b.status) && <button disabled={busy} onClick={() => action(() => post('/meeting'))}><Video size={18} /> Создать встречу</button>}{role === 'specialist' && b.status === 'pending' && <><button disabled={busy} onClick={() => action(() => axios.patch(endpoint, {
              status: 'confirmed'
            }, {
              withCredentials: true,
              timeout: 30000
            }))}>Подтвердить</button><button className="secondary" disabled={busy} onClick={() => action(() => axios.patch(endpoint, {
              status: 'rejected'
            }, {
              withCredentials: true,
              timeout: 30000
            }))}>Не подтверждать</button></>}{role === 'specialist' && b.status === 'paid' && <button disabled={busy} onClick={() => action(() => axios.patch(endpoint, {
            status: 'completed'
          }, {
            withCredentials: true,
            timeout: 30000
          }))}>Отметить проведённой</button>}{role === 'specialist' && b.status === 'completed' && <><button className="secondary" disabled={busy} onClick={() => action(() => axios.patch(endpoint, {
            status: 'confirmed'
          }, {
            withCredentials: true,
            timeout: 30000
          }))}>Вернуть в работу</button></>}{active && <button className="danger" disabled={busy} onClick={() => {
            if (window.confirm('Отменить консультацию? Если она оплачена, возврат нужно согласовать со специалистом.')) action(() => post('/cancel'));
          }}>Отменить консультацию</button>}{['refund_required', 'partially_refunded', 'refunded'].includes(b.payment_status) && <p className="consult-muted">Отмена не выполняет возврат автоматически. Согласуйте возврат в переписке.</p>}</section>{role === 'specialist' && <section className="consult-panel"><h2>Настройки специалиста</h2>{['pending', 'confirmed'].includes(b.status) && <button className="secondary" disabled={busy} onClick={() => setEdit({
            booking_date: b.booking_date,
            start_time: b.start_time.slice(0, 5),
            end_time: b.end_time.slice(0, 5),
            format: b.format,
            amount: b.amount || ''
          })}>Изменить дату и стоимость</button>}{edit && <form onSubmit={e => {
            e.preventDefault();
            action(async () => {
              await axios.patch(endpoint, edit, {
                withCredentials: true,
                timeout: 30000
              });
              setEdit(null);
            });
          }}><p className="consult-muted">Укажите начало и окончание встречи. Всё время между ними будет занято этой консультацией. Стоимость — за всю встречу.</p><label>Дата<input type="date" required value={edit.booking_date} onChange={e => setEdit({
                ...edit,
                booking_date: e.target.value
              })} /></label><label>Начало (МСК)<input type="time" required value={edit.start_time} onChange={e => setEdit({...edit, start_time: e.target.value})} /></label><label>Окончание (МСК)<input type="time" required value={edit.end_time} onChange={e => setEdit({
                ...edit,
                end_time: e.target.value
              })} /></label><label>Формат<select value={edit.format} onChange={e => setEdit({
                ...edit,
                format: e.target.value
              })}><option value="online">Онлайн</option><option value="offline">Очно</option></select></label><label>Стоимость, ₽<input type="number" min="1" max="1000000" step="0.01" required value={edit.amount} onChange={e => setEdit({
                ...edit,
                amount: e.target.value
              })} /></label><button disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button><button type="button" className="secondary" disabled={busy} onClick={() => setEdit(null)}>Закрыть</button></form>}<label>Личные заметки<textarea value={notes} onChange={e => setNotes(e.target.value)} maxLength={10000} rows={5} /></label><p className="consult-muted">Клиент не видит эти заметки.</p><button disabled={busy} onClick={() => action(() => axios.patch(endpoint, {
            specialist_notes: notes
          }, {
            withCredentials: true,
            timeout: 30000
          }))}>Сохранить заметки</button></section>}</aside></div></div>;
}
